import { Link, useLocation } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import ButtonAndLinks from '#app/components/typography/button-and-links'
import PlentyBody from '#app/components/typography/plenty-body'
import { useSalesFlags } from '#app/hooks/use-sales-flag'
import { cn } from '#app/utils/misc'

// Helper function to find the parent category and its subcategories

const getParentCategory = (
	categories: any[],
	currentCategory: { parent: any },
) => {
	const parentCategory = categories.find(
		(cat: { id: any }) => cat.id === currentCategory.parent,
	)
	return parentCategory
}

const findParentCategory = (
	categories: any[],
	currentCategory: { parent: any },
) => {
	const parentCategory = categories.find(
		(cat: { id: any }) => cat.id === currentCategory.parent,
	)

	return parentCategory
		? categories.filter(
				(cat: { parent: any }) => cat.parent === parentCategory.id,
		  )
		: []
}

const findAdjacentCategories = (category: any, allCategories: any): any[] => {
	const subCategoriesFromParent = findParentCategory(allCategories, category)

	if (!subCategoriesFromParent) {
		return []
	}
	return subCategoriesFromParent.filter((cat: any) => cat !== category)
}

const sortCategories = (categories: any[]) => {
	const clothingCategories = categories.filter(cat =>
		cat.id.includes('clothing'),
	)
	const outerwearCategories = categories.filter(cat =>
		cat.id.includes('outerwear'),
	)
	const accessoriesCategories = categories.filter(cat =>
		cat.id.includes('accessories'),
	)
	const otherCategories = categories.filter(
		cat =>
			!cat.id.includes('clothing') &&
			!cat.id.includes('outerwear') &&
			!cat.id.includes('accessories'),
	)

	const sortedClothingCategories = clothingCategories.sort((a, b) =>
		a.name.localeCompare(b.name),
	)
	const sortedOuterwearCategories = outerwearCategories.sort((a, b) =>
		a.name.localeCompare(b.name),
	)
	const sortedAccessoriesCategories = accessoriesCategories.sort((a, b) =>
		a.name.localeCompare(b.name),
	)
	const sortedOtherCategories = otherCategories.sort((a, b) =>
		a.name.localeCompare(b.name),
	)

	return [
		...sortedClothingCategories,
		...sortedOuterwearCategories,
		...sortedAccessoriesCategories,
		...sortedOtherCategories,
	]
}
const CategorySelectorClerkV2 = ({
	category,
	allCategories,
	subCategories,
	showMainCategory,
}: {
	category: any
	allCategories: any
	subCategories: any
	showMainCategory?: boolean
}) => {
	const location = useLocation()
	const searchParams = new URLSearchParams(location.search)
	const brandParams = searchParams.getAll('brand')
	const genderParams = searchParams.getAll('gender')
	const sortingParams = searchParams.get('sortingOrder')
	const storeParams = searchParams.get('store')?.split(',') || []
	const isLicensedParam = searchParams.get('isLicensed')
	let { t } = useTranslation('categories')
	const parentCategory = getParentCategory(allCategories, category)
	const categoryName = category.name
	const params: string[] = []
	if (brandParams) {
		const brandParamsString = brandParams
			.map(brand => `brand=${encodeURIComponent(brand)}`)
			.join('&')
		if (brandParamsString) {
			params.push(brandParamsString)
		}
	}
	if (genderParams) {
		const genderParamsString = genderParams
			.map(gender => `gender=${encodeURIComponent(gender)}`)
			.join('&')
		if (genderParamsString) {
			params.push(genderParamsString)
		}
	}
	if (storeParams) {
		const storeParamsString = storeParams
			.map(store => `${encodeURIComponent(store)}`)
			.join(',')
		if (storeParamsString) {
			params.push(`store=${storeParamsString}`)
		}
	}
	if (sortingParams) {
		params.push(`sortingOrder=${sortingParams}`)
	}
	if (isLicensedParam) {
		params.push(`isLicensed=${isLicensedParam}`)
	}
	const paramsForUrl = params.length > 0 ? `?${params.join('&')}` : ''
	const mainCategories = ['women', 'men', 'kids']
	const isMainCategory = mainCategories.includes(category.id)

	// Define the specific subcategories for kids
	const kidsSubCategories = [
		{ id: 'kids-baby_0_2', name: t('baby_0_2') },
		{ id: 'kids-girl_2_8', name: t('girl_2_8') },
		{ id: 'kids-boy_2_8', name: t('boy_2_8') },
		{ id: 'kids-girl_9_16', name: t('girl_9_16') },
		{ id: 'kids-boy_9_16', name: t('boy_9_16') },
	]

	// Filter the subcategories to include only the specified kids subcategories
	const filteredSubCategories =
		isMainCategory && category.id === 'kids' ? kidsSubCategories : subCategories

	// Check if the category is at the third level (has three hyphens)
	const isThirdLevelCategory = (category.id.match(/-/g) || []).length === 3

	// If there are no subcategories and it's a third-level category, find the siblings of the parent category
	const displaySubCategories =
		filteredSubCategories.length === 0 && isThirdLevelCategory
			? findParentCategory(allCategories, category)
			: filteredSubCategories
	const plpPath = location.pathname.split('/').slice(0, -1).join('/')
	// Remove only the current category from the subcategories list
	const removedDuplicatedCategoryFromSubCategories =
		filteredSubCategories.length > 0
			? displaySubCategories.filter(
					(subCategory: any) => subCategory.name !== category.name,
			  )
			: findAdjacentCategories(category, allCategories)

	// Extract store from URL parameters
	const storeArg = storeParams.length > 0 ? storeParams.join(',') : undefined
	const shouldUseStoreContext = storeParams.length > 0

	// Extract brand parameter
	const brandParam = searchParams.get('brand')

	// Use our custom hook to get sales flags
	// The hook already handles checking for brand-specific sales
	const { hasWomenSales, hasMenSales, hasKidsSales } = useSalesFlags(
		storeArg,
		shouldUseStoreContext,
	)

	const hasSalesForCategory = (categoryId: string) => {
		// First check if this is a gender-specific category with known sales
		if (categoryId === 'women' || categoryId.startsWith('women-'))
			return hasWomenSales
		if (categoryId === 'men' || categoryId.startsWith('men-'))
			return hasMenSales
		if (categoryId === 'kids' || categoryId.startsWith('kids-'))
			return hasKidsSales

		// For other categories, show sales if we're in a store context or have brand param and any gender has sales
		if (shouldUseStoreContext || brandParam) {
			const result = hasWomenSales || hasMenSales || hasKidsSales
			return result
		}

		return false
	}

	return (
		<div
			className={cn(
				'mt-4 flex flex-col items-center space-y-4',
				isMainCategory ? 'mb-4 md:mb-0' : 'mb-8 md:mb-0',
			)}
		>
			{showMainCategory && isMainCategory && (
				<div className="flex w-full items-center justify-center ">
					<PlentyBody size="md" className="font-bold">
						{categoryName}
					</PlentyBody>
				</div>
			)}

			{!isMainCategory && (
				<div
					className={cn('flex flex-col items-center justify-center space-y-1')}
				>
					{parentCategory && (
						<Link to={`${plpPath}/${parentCategory.id}${paramsForUrl}`}>
							<PlentyBody size="sm" className="text-beige-100">
								{parentCategory.name}
							</PlentyBody>
						</Link>
					)}
					<div className="flex w-full items-center justify-center ">
						<PlentyBody size="md" className="font-bold" primaryHeading>
							{categoryName}
						</PlentyBody>
					</div>
				</div>
			)}

			{categoryName && (
				<div
					className="mx-auto flex w-full place-items-center items-center space-x-2 overflow-x-auto whitespace-nowrap scrollbar-hide md:flex-wrap md:justify-center"
					data-testid="plp-subcategories-container"
				>
					{!plpPath.includes('sale') &&
						(isMainCategory ||
							category.id.startsWith('women-') ||
							category.id.startsWith('men-') ||
							category.id.startsWith('kids-')) && (
							<>
								{hasSalesForCategory(category.id) && (
									<div>
										<Link
											to={`/products-sale/${category.id}${paramsForUrl}`}
											className="group flex items-center rounded-sm border border-beige-70 px-2 py-1 transition-all md:hover:border-transparent md:hover:bg-black-90 md:hover:text-white"
										>
											<ButtonAndLinks
												className="text-[14px] leading-[1.375rem]"
												body={t('navbar:header_sale')}
											/>
										</Link>
									</div>
								)}
							</>
						)}
					{(category.id === 'men' ||
					category.id === 'women' ||
					(category.id === 'kids ' && category.id.split('-').length === 2)
						? sortCategories(removedDuplicatedCategoryFromSubCategories)
						: removedDuplicatedCategoryFromSubCategories
					).map((subCategory: any) => {
						const { pathname } = location

						const pathSegments = pathname.split('/')
						pathSegments.pop() // Remove the last segment

						const url = `${pathSegments.join('/')}/${
							subCategory.id
						}${paramsForUrl}`
						return (
							<div key={subCategory.id}>
								<Link
									to={url}
									className="group flex items-center rounded-sm border border-beige-70 px-2 py-1 transition-all md:hover:border-transparent md:hover:bg-black-90 md:hover:text-white"
									data-testid={`plp-subcategory-${subCategory.name
										.replace(' ', '')
										.toLowerCase()}`}
								>
									<ButtonAndLinks
										className="text-[14px] leading-[1.375rem]"
										body={subCategory.name}
									/>
								</Link>
							</div>
						)
					})}
				</div>
			)}
		</div>
	)
}

export default CategorySelectorClerkV2
